import CardFrameSurface from "./CardFrameSurface";

const WALL_CARD_SIZE = {
  mobile: 156,
  desktop: 210,
  viewportWidth: 40,
  aspectRatio: 247 / 160
};

const WALL_CARD_TIMING = {
  scrollDuration: "56s",
  swayDuration: "10s",
  revealDuration: "56s"
};

const PUBLIC_WALL_CARDS = [
  {
    question: "What's a conversation you're glad we had as a family, even though it might have been difficult?",
    translation: "비록 어려웠을지라도, 가족으로서 나눠서 다행이라고 생각하는 대화는 무엇인가요?",
    top: "0%",
    rotate: "-8deg",
    sway: "10px",
    delay: "-2s"
  },
  {
    question: "Is there someone in your life who was good at listening to you? What did they do that made you feel heard?",
    translation: "在您的生活中，有没有人善于倾听您心声？他们做了什么让您感觉被倾听了?",
    top: "70%",
    rotate: "7deg",
    sway: "-12px",
    delay: "-16s"
  },
  {
    question: "What's a childhood memory that still brings you joy?",
    translation: "¿Cuál es un recuerdo de su infancia que todavía le trae alegría?",
    top: "12%",
    rotate: "-5deg",
    sway: "14px",
    delay: "-20s"
  },
  {
    question: "How should we signal when there's something one of us wants to say that's hard to bring up?",
    translation: "가족중 누군가가 꺼내기 어려운 이야기가 있을 때, 어떻게 다가가면 좋을까요?",
    top: "68%",
    rotate: "-5deg",
    sway: "14px",
    delay: "-30s"
  },
  {
    question: "When have you seen our family come together during challenging times?",
    translation: "어려운 시기에 우리 가족이 함께 뭉쳤던 때는 언제인가요?",
    top: "60%",
    rotate: "10deg",
    sway: "-9px",
    delay: "-44s"
  }
];

const WallMotionStyles = () => (
  <style>
    {`
      @keyframes wall-card-scroll {
        0% { transform: translate3d(-250px, 0, 0); opacity: 0; }
        8% { opacity: 1; }
        86% { opacity: 1; }
        100% { transform: translate3d(calc(100% + 250px), 0, 0); opacity: 0; }
      }

      @keyframes wall-card-sway {
        0%, 100% { transform: translate3d(0, 0, 0) rotate(var(--card-rotate)); }
        28% { transform: translate3d(0, var(--card-sway), 0) rotate(calc(var(--card-rotate) + 2deg)); }
        58% { transform: translate3d(0, calc(var(--card-sway) * -0.65), 0) rotate(calc(var(--card-rotate) - 1.4deg)); }
        82% { transform: translate3d(0, calc(var(--card-sway) * 0.35), 0) rotate(calc(var(--card-rotate) + 0.8deg)); }
      }

      @keyframes carried-card-reveal {
        0%, 8% { opacity: 0; transform: translateY(14px) scale(0.94); }
        16%, 82% { opacity: 1; transform: translateY(0) scale(1); }
        100% { opacity: 0.92; transform: translateY(6px) scale(0.98); }
      }

      @keyframes light-thread-drift {
        0%, 100% { transform: translate3d(-3%, 0, 0); opacity: 0.62; }
        50% { transform: translate3d(3%, -10px, 0); opacity: 0.92; }
      }

      @media (prefers-reduced-motion: reduce) {
        .mcaad-motion-card,
        .mcaad-light-thread {
          animation: none !important;
        }
      }
    `}
  </style>
);

const DigitalWallBackground = ({ className = "" }) => {
  const cardWidthValue = `clamp(${WALL_CARD_SIZE.mobile}px, ${WALL_CARD_SIZE.viewportWidth}vw, ${WALL_CARD_SIZE.desktop}px)`;
  const cardHeightValue = `clamp(${Math.round(WALL_CARD_SIZE.mobile * WALL_CARD_SIZE.aspectRatio)}px, ${WALL_CARD_SIZE.viewportWidth * WALL_CARD_SIZE.aspectRatio}vw, ${Math.round(WALL_CARD_SIZE.desktop * WALL_CARD_SIZE.aspectRatio)}px)`;

  return (
    <div className={`absolute inset-0 overflow-hidden bg-[#fbf4d6] shadow-[inset_0_0_70px_rgba(255,255,255,0.6)] ${className}`} aria-hidden="true">
      <WallMotionStyles />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_20%,rgba(255,255,255,0.98)_0%,rgba(255,255,255,0.7)_13%,transparent_31%),radial-gradient(circle_at_52%_45%,rgba(255,255,255,0.62)_0%,rgba(255,255,255,0.28)_22%,transparent_42%),radial-gradient(circle_at_30%_55%,rgba(229,195,79,0.34),transparent_30%),radial-gradient(circle_at_82%_66%,rgba(255,255,255,0.34),transparent_28%),linear-gradient(180deg,#fff7d2_0%,#f4e7af_55%,#efe1b5_100%)]" />
      <div className="absolute left-1/2 top-[18%] aspect-square w-80 -translate-x-1/2 rounded-full border border-white/50 bg-white/20 shadow-[0_0_95px_rgba(255,255,255,0.9)] sm:w-[430px]" />
      <div className="absolute left-[18%] top-[30%] aspect-square w-36 rounded-full bg-white/34 blur-2xl sm:w-52" />
      <div className="absolute right-[12%] top-[36%] aspect-square w-40 rounded-full bg-[#fff8cf]/42 blur-2xl sm:w-60" />
      <div className="absolute left-[42%] top-[58%] aspect-square w-44 -translate-x-1/2 rounded-full bg-white/28 blur-3xl sm:w-64" />
      <div className="absolute right-[16%] bottom-[12%] aspect-square w-32 rounded-full bg-[#fffdf1]/34 blur-2xl sm:w-48" />
      <div className="absolute left-1/2 top-[36%] aspect-square w-56 -translate-x-1/2 rounded-full bg-[#fff4ad]/22 blur-3xl sm:w-72" />
      <div className="absolute inset-x-0 top-0 h-56 bg-gradient-to-b from-[#fff8d8] via-[#fff8d8]/72 to-transparent" />
      <div className="absolute inset-x-0 bottom-0 h-56 bg-gradient-to-t from-[#efe1b5] via-[#efe1b5]/82 to-transparent" />

      <svg className="mcaad-light-thread pointer-events-none absolute inset-x-[-18%] top-[17%] z-10 h-[54%] w-[136%] overflow-visible opacity-80" viewBox="0 0 1400 520" preserveAspectRatio="none" fill="none" style={{ animation: "light-thread-drift 22s ease-in-out infinite" }}>
        <path d="M-70 130 C140 18 258 196 446 104 C614 22 748 66 908 146 C1074 230 1204 128 1470 70" stroke="rgba(255,255,255,0.72)" strokeWidth="3" strokeLinecap="round" />
        <path d="M-80 234 C92 136 250 256 428 196 C630 128 768 166 930 254 C1110 350 1244 224 1480 176" stroke="rgba(255,255,255,0.48)" strokeWidth="2.5" strokeLinecap="round" />
        <path d="M-60 324 C154 250 286 364 488 292 C690 220 808 282 1008 334 C1192 382 1284 294 1470 252" stroke="rgba(255,255,255,0.38)" strokeWidth="2" strokeLinecap="round" />
        <path d="M50 22 C174 168 326 132 464 42" stroke="rgba(255,255,255,0.36)" strokeWidth="2" strokeLinecap="round" />
        <path d="M860 34 C1016 190 1164 170 1348 66" stroke="rgba(255,255,255,0.34)" strokeWidth="2" strokeLinecap="round" />
        <circle cx="222" cy="88" r="5" fill="white" />
        <circle cx="222" cy="88" r="18" fill="rgba(255,255,255,0.28)" />
        <circle cx="694" cy="136" r="4" fill="white" />
        <circle cx="694" cy="136" r="16" fill="rgba(255,255,255,0.24)" />
        <circle cx="1128" cy="226" r="5" fill="white" />
        <circle cx="1128" cy="226" r="20" fill="rgba(255,255,255,0.24)" />
        <circle cx="418" cy="292" r="3.5" fill="white" />
        <circle cx="418" cy="292" r="14" fill="rgba(255,255,255,0.22)" />
      </svg>

      <div className="absolute inset-0 z-20">
        {PUBLIC_WALL_CARDS.map((card) => (
          <div
            key={card.question}
            className="mcaad-motion-card absolute left-0 w-full"
            style={{
              top: card.top,
              animation: `wall-card-scroll ${WALL_CARD_TIMING.scrollDuration} linear infinite`,
              animationDelay: card.delay
            }}
          >
            <div
              className="relative flex flex-col items-center"
              style={{
                width: cardWidthValue,
                "--card-rotate": card.rotate,
                "--card-sway": card.sway,
                animation: `wall-card-sway ${WALL_CARD_TIMING.swayDuration} ease-in-out infinite`,
                animationDelay: card.delay
              }}
            >
              <div className="absolute left-1/2 top-1/2 z-0 h-56 w-56 -translate-x-1/2 -translate-y-1/2 rounded-full bg-white/42 blur-2xl sm:h-72 sm:w-72" />
              <div className="absolute left-1/2 top-1/2 z-0 h-24 w-24 -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#fffdf1]/72 blur-xl sm:h-32 sm:w-32" />
              <CardFrameSurface
                className="mcaad-motion-card relative z-10 drop-shadow-[0_24px_46px_rgba(92,77,22,0.26)]"
                style={{
                  width: cardWidthValue,
                  height: cardHeightValue,
                  animation: `carried-card-reveal ${WALL_CARD_TIMING.revealDuration} linear infinite`,
                  animationDelay: card.delay
                }}
              >
                <div className="absolute inset-x-[18%] top-[14%] bottom-[23%] flex flex-col items-center justify-center gap-1.5 text-center">
                  <span className="font-sans text-[11px] font-bold leading-tight text-[#2f2a16] sm:text-[15px]" style={{ fontFamily: "'Poppins', sans-serif" }}>
                    {card.question}
                  </span>
                  <span className="font-sans text-[7px] font-medium leading-snug text-[#5f5942] sm:text-[8.5px]" style={{ fontFamily: "'Poppins', sans-serif" }}>
                    {card.translation}
                  </span>
                </div>
              </CardFrameSurface>
            </div>
          </div>
        ))}
      </div>

      <div className="absolute inset-0 z-30 bg-[radial-gradient(circle_at_50%_34%,rgba(255,255,255,0.01),transparent_24%),linear-gradient(180deg,rgba(255,253,241,0)_0%,rgba(255,253,241,0.06)_100%)]" />
    </div>
  );
};

export default DigitalWallBackground;
